import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, MessageCircle, Share2, ArrowRight, CheckCircle2, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getParticipantId, clearParticipantId } from "@/lib/userInterviews";
import {
    isProlificTester,
    clearProlificSource,
    getProlificContext,
} from "@/lib/prolific";
import { useStudyConfig } from "@/lib/useStudyConfig";

/**
 * /study-complete — internal landing page testers see when they finish.
 *
 * Replaces the old redirect to User Interviews' completion endpoint. Everything
 * we need is already in the admin dashboard (Admin → Beta activity → funnel),
 * keyed by the iid in the URL.
 *
 * The Prolific completion code + URL come from useStudyConfig() so the
 * founder can swap to a new Prolific study from /admin without a
 * redeploy. Falls back to the compile-time constant if the network is
 * cold.
 */
export default function StudyComplete() {
    const [params] = useSearchParams();
    const iid = (params.get("iid") || getParticipantId() || "").trim();
    const fromProlific = isProlificTester();
    const studyConfig = useStudyConfig();
    const completionCode = studyConfig.completion_code;
    const completionUrl = studyConfig.completion_url;

    // Instrumentation — log "completion_page_viewed" on mount. The
    // Prolific Diagnostics view uses this to confirm the participant
    // actually saw the completion code before redirecting (if this
    // event is missing, the gap is between feedback_saved and here).
    React.useEffect(() => {
        if (!fromProlific || !iid) return;
        const ctx = getProlificContext();
        api.post("/prolific/track-step", {
            step: "completion_page_viewed",
            participant_id: iid,
            study_id: ctx.studyId,
            session_id: ctx.sessionId,
            completion_code: completionCode,
        }).catch(() => {});
    }, [fromProlific, iid, completionCode]);

    // Fired the moment the participant clicks "Return to Prolific". The
    // Diagnostics view treats this as the last confirmed step before
    // the participant is in Prolific's hands.
    const logRedirectClick = () => {
        if (!fromProlific || !iid) return;
        const ctx = getProlificContext();
        // Best-effort; navigator.sendBeacon would be cleaner but isn't
        // wired through our axios layer. The click takes them off-site
        // so we don't await this.
        api.post("/prolific/track-step", {
            step: "prolific_redirect_clicked",
            participant_id: iid,
            study_id: ctx.studyId,
            session_id: ctx.sessionId,
            completion_code: completionCode,
        }).catch(() => {});
    };

    const shareLink = "https://www.memorylanecare.net";
    const shareText = "I just spent 15 min testing Memory Lane — a free tool for dementia caregivers. It feels like it actually gets it. Worth a look:";

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(`${shareText} ${shareLink}`);
            toast.success("Copied. Paste it anywhere.");
        } catch {
            toast.error("Couldn't copy. You can highlight and copy manually below.");
        }
    };

    const copyProlificCode = async () => {
        try {
            await navigator.clipboard.writeText(completionCode);
            toast.success("Completion code copied — paste it into Prolific.");
        } catch {
            toast.error("Couldn't copy. Highlight the code and copy manually.");
        }
    };

    // Clear the iid so the tester panel + Finish-study button stop showing.
    // The data is already saved in admin; the local id has done its job.
    const finish = () => {
        clearParticipantId();
        clearProlificSource();
        try {
            // Also clear per-iid tester task progress.
            Object.keys(localStorage)
                .filter(k => iid && k.includes(`mlcn_tester_tasks_${iid}`))
                .forEach(k => localStorage.removeItem(k));
        } catch { /* noop */ }
    };

    return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6" data-testid="study-complete-page">
            <div className="max-w-xl w-full text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-stage_early-light mb-6">
                    <CheckCircle2 size={36} strokeWidth={1.5} className="text-stage_early-dark" />
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-[#12776F]">
                    Thank you.
                </h1>
                <p className="mt-4 text-lg text-[#4C5551] leading-relaxed">
                    Because of the answers you just gave, the next caregiver who lands here in the middle
                    of a hard night gets a better Memory Lane. The daughter Googling at 2 AM. The son
                    trying to talk to his sister about Mom&apos;s care. The grandchild stepping in for the
                    first time. They&apos;ll have an easier time because you showed up today.
                </p>

                {fromProlific && (
                    <div
                        className="mt-8 ml-card p-6 text-left !border-stage_late-light bg-stage_late-light/40"
                        data-testid="prolific-completion-block"
                    >
                        <div className="flex items-start gap-3">
                            <div className="shrink-0 w-10 h-10 rounded-2xl bg-stage_late-dark text-white flex items-center justify-center">
                                <CheckCircle2 strokeWidth={1.8} size={18} />
                            </div>
                            <div className="flex-1">
                                <h2 className="font-heading font-medium text-lg text-[#1F2624]">
                                    Your Prolific completion code
                                </h2>
                                <p className="mt-1 text-sm text-[#4C5551]">
                                    Copy this code and paste it into Prolific to mark this study as
                                    complete. Without it, Prolific won&apos;t release your payment.
                                </p>
                            </div>
                        </div>
                        <div className="mt-4 bg-white border-2 border-stage_late-dark rounded-2xl px-4 py-4 flex flex-wrap items-center justify-between gap-3">
                            <code
                                data-testid="prolific-completion-code"
                                className="font-mono text-2xl sm:text-3xl tracking-widest text-stage_late-dark font-semibold select-all"
                            >
                                {completionCode}
                            </code>
                            <button
                                type="button"
                                onClick={copyProlificCode}
                                data-testid="prolific-copy-code"
                                className="inline-flex items-center justify-center gap-2 bg-stage_late-dark hover:bg-stage_late-dark/90 active:bg-stage_late-dark/80 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px]"
                            >
                                <Copy size={14} strokeWidth={2} /> Copy code
                            </button>
                        </div>
                        <a
                            href={completionUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={logRedirectClick}
                            data-testid="prolific-return-link"
                            className="mt-3 inline-flex items-center justify-center gap-2 bg-white hover:bg-cream-dark active:bg-cream-dark/80 border border-stage_late-dark text-stage_late-dark rounded-2xl px-5 py-3 text-sm font-medium w-full min-h-[44px]"
                        >
                            Return to Prolific
                            <ExternalLink size={13} strokeWidth={1.8} />
                        </a>
                        <p className="mt-3 text-xs text-[#4C5551] leading-snug">
                            If the &quot;Return to Prolific&quot; button doesn&apos;t work, just copy the code
                            above and paste it into the completion-code box on Prolific&apos;s study page.
                        </p>
                    </div>
                )}

                <div className="mt-8 ml-card p-6 text-left">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-2xl bg-coral-light flex items-center justify-center">
                            <Heart strokeWidth={1.7} className="text-coral-dark" />
                        </div>
                        <div>
                            <h2 className="font-heading font-medium text-lg text-[#1F2624]">
                                One more thing — if it would help someone you know
                            </h2>
                            <p className="mt-1 text-sm text-[#4C5551]">
                                You can&apos;t share Memory Lane fast enough for the caregiver who needs it tonight.
                                Send them a link.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={copyLink}
                        data-testid="study-complete-copy-link"
                        className="ml-btn-primary mt-5 w-full justify-center"
                    >
                        <Share2 size={16} strokeWidth={1.8} /> Copy a share link
                    </button>
                    <p className="mt-3 text-xs text-[#6E7A74] font-mono text-center break-all" data-testid="study-complete-share-url">
                        {shareLink}
                    </p>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                        to="/feedback"
                        onClick={finish}
                        data-testid="study-complete-more-feedback"
                        className="ml-btn-secondary justify-center"
                    >
                        <MessageCircle size={16} strokeWidth={1.8} /> Add one more thought
                    </Link>
                    <Link
                        to="/dashboard"
                        onClick={finish}
                        data-testid="study-complete-keep-using"
                        className="ml-btn-lavender justify-center"
                    >
                        Keep using Memory Lane <ArrowRight size={16} strokeWidth={1.8} />
                    </Link>
                </div>

                {iid && (
                    <p className="mt-8 text-xs text-[#6E7A74]" data-testid="study-complete-iid-note">
                        Session id: <code className="font-mono">{iid}</code>
                    </p>
                )}
            </div>
        </div>
    );
}
